import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, AlertTriangle, Package as PackageIcon, Search } from "lucide-react";
import { inr } from "@/utils";
import { toast } from "sonner";
import { usePagination } from "@/components/Pagination";

const CATEGORIES = ["Cement", "Steel", "Sand", "Bricks", "Tiles", "Paint", "Electrical", "Plumbing", "Doors", "Windows", "Glass", "Granite", "Marble", "Hardware", "Consumables"];

export default function Materials() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [projectId, setProjectId] = useState("");
  const [search, setSearch] = useState("");
  const { data: mats = [] } = useQuery({
    queryKey: ["materials", projectId],
    queryFn: async () => (await api.get("/materials", { params: projectId ? { project_id: projectId } : {} })).data,
  });

  const filteredMats = mats.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (m.name || "").toLowerCase().includes(q) ||
      (m.item_code || "").toLowerCase().includes(q) ||
      (m.category || "").toLowerCase().includes(q) ||
      (m.warehouse || "").toLowerCase().includes(q)
    );
  });

  const { pageRows, controls } = usePagination(filteredMats);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", item_code: "", category: "Cement", uom: "bags", opening_stock: 0, reorder_level: 0, avg_cost: 0, purchase_rate: 0, warehouse: "Main Warehouse", supplier: "" });

  async function save() {
    if (!form.name) return toast.error("Material name required");
    try {
      await api.post("/materials", {
        ...form,
        project_id: projectId || null,
        opening_stock: +form.opening_stock, reorder_level: +form.reorder_level,
        avg_cost: +form.avg_cost, purchase_rate: +form.purchase_rate,
        unit: form.uom,
      });
      toast.success("Material added");
      qc.invalidateQueries({ queryKey: ["materials"] });
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="materials-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Construction</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Material Inventory</h1>
          <p className="text-sm text-zinc-500 mt-1">Cement, steel, tiles, paint — opening / purchased / consumed / closing.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              placeholder="Search material, code, category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-64 pl-8"
              data-testid="material-search"
            />
          </div>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-64" data-testid="material-project"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-material-btn"><Plus size={14} className="mr-1" /> Add Material</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Add Material</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mat-name" /></div>
                <div><Label>Item Code</Label><Input value={form.item_code} onChange={(e) => setForm({ ...form, item_code: e.target.value })} placeholder="auto if blank" /></div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Unit of Measure</Label><Input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} placeholder="bags, MT, kg, sqft, nos" /></div>
                <div><Label>Warehouse</Label><Input value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} /></div>
                <div><Label>Opening Stock</Label><Input type="number" value={form.opening_stock} onChange={(e) => setForm({ ...form, opening_stock: e.target.value })} /></div>
                <div><Label>Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></div>
                <div><Label>Purchase Rate (₹)</Label><Input type="number" value={form.purchase_rate} onChange={(e) => setForm({ ...form, purchase_rate: e.target.value })} /></div>
                <div><Label>Avg Cost (₹)</Label><Input type="number" value={form.avg_cost} onChange={(e) => setForm({ ...form, avg_cost: e.target.value })} /></div>
                <div className="col-span-2"><Label>Preferred Supplier</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-material">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>Code</TableHead><TableHead>Material</TableHead><TableHead>Category</TableHead><TableHead>UoM</TableHead>
              <TableHead className="text-right">Current Stock</TableHead>
              <TableHead className="text-right">Reorder Lvl</TableHead>
              <TableHead className="text-right">Avg Cost</TableHead><TableHead className="text-right">Stock Value</TableHead>
              <TableHead>Warehouse</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pageRows.map((m) => {
                const stock = Number(m.current_stock ?? m.closing_stock ?? 0);
                const value = stock * (m.avg_cost || 0);
                const low = stock < (m.reorder_level || 0);
                return (
                  <TableRow key={m.id} className="data-row" data-testid={`material-row-${m.id}`}>
                    <TableCell className="text-xs font-mono">{m.item_code || "—"}</TableCell>
                    <TableCell className="font-medium flex items-center gap-2">
                      {low ? <AlertTriangle size={13} className="text-amber-600" /> : <PackageIcon size={13} className="text-zinc-400" />}
                      {m.name}
                    </TableCell>
                    <TableCell><span className="text-[11px] uppercase tracking-widest bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded">{m.category}</span></TableCell>
                    <TableCell className="text-xs">{m.uom || m.unit}</TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${low ? "text-amber-700" : ""}`}>{stock}</TableCell>
                    <TableCell className="text-right font-mono text-zinc-500">{m.reorder_level || 0}</TableCell>
                    <TableCell className="text-right font-mono">{inr(m.avg_cost)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{inr(value)}</TableCell>
                    <TableCell className="text-xs">{m.warehouse || "—"}</TableCell>
                  </TableRow>
                );
              })}
              {!filteredMats.length && <TableRow><TableCell colSpan={9} className="text-center text-sm text-zinc-500 py-10">{search ? `No materials matching "${search}".` : "No materials in master. Add one, then post purchases to build up stock automatically."}</TableCell></TableRow>}
            </TableBody>
          </Table>
          {controls}
        </CardContent>
      </Card>
    </div>
  );
}
